/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { I18nService, LazyloadService, PermissionService, UtilService } from '@dm/core';
import { ConfigureDnsComponent } from 'app/feature/service/v-store-service/v-store/detail-v-store/detail-service/dns/configure-dns/configure-dns.component';
import { FileDnsServiceService } from 'app/feature/settings/file-storage-service/file-dns-service/file-dns-service.service';
import { ConfigureServiceAdComponent } from 'app/feature/settings/users-security/domain-auth/service-ad/configure-service-ad/configure-service-ad.component';
import { ServiceAdService } from 'app/feature/settings/users-security/domain-auth/service-ad/service-ad.service';
import { ConfigureServiceLdapComponent } from 'app/feature/settings/users-security/domain-auth/service-ldap/configure-service-ldap/configure-service-ldap.component';
import { ServiceLdapService } from 'app/feature/settings/users-security/domain-auth/service-ldap/service-ldap.service';
import { ConfigureServiceNisComponent } from 'app/feature/settings/users-security/domain-auth/service-nis/configure-service-nis/configure-service-nis.component';
import { ServiceNisService } from 'app/feature/settings/users-security/domain-auth/service-nis/service-nis.service';
import { type Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/**
 * 支持的检查项
 */
export enum GuideItemE {
    DNS = 'DNS',
    LDAP = 'LDAP',
    NIS = 'NIS',
    AD = 'AD'
}

/**
 * 配置项
 */
export interface GuideConfig {
    name: GuideItemE;
    label: string;
    configured: boolean;
    /**
     * 存在表示没有权限
     */
    permissionTips: string;
    action: (cb: (...args) => void) => void;
}


@Injectable({
    providedIn: 'root',
})
export class FileSystemGuideService {
    private _serviceMap = {
        [GuideItemE.DNS]: this.getDnsConfig,
        [GuideItemE.NIS]: this.getNisDomainConfig,
        [GuideItemE.AD]: this.getAdDomainConfig,
        [GuideItemE.LDAP]: this.getLDAPDomainConfig,
    };

    constructor(
        public permissionService: PermissionService,
        private serviceLdapService: ServiceLdapService,
        private serviceNisService: ServiceNisService,
        private serviceAdService: ServiceAdService,
        public fileDnsService: FileDnsServiceService,
        public lazyLoadService: LazyloadService,
        public util: UtilService,
        public i18nService: I18nService,
    ) { }

    hasDomainReadPermission = this.permissionService.hasPermission('domain_R');

    getConfigureConfig(items: GuideItemE[] = [], vstoreId: string): Observable<GuideConfig[]> {
        const cmds: Observable<GuideConfig>[] = items.map((item) => this._serviceMap[item].call(this, vstoreId));

        return forkJoin(cmds);
    }

    getDnsConfig(vstoreId: string): Observable<GuideConfig> {
        const config: GuideConfig = {
            name: GuideItemE.DNS,
            label: this.i18nService.get('service.share_term_addClient_configDomain_DNS'),
            configured: false,
            permissionTips: this.getNopermissionTips(['domain_W']),
            action: (cb) => {
                this.lazyLoadService.appendComponent(ConfigureDnsComponent, {
                    vstoreId,
                    onClose: () => cb(),
                });
            },
        };

        if (!this.hasDomainReadPermission) {
            return of(config);
        }

        return this.fileDnsService.getDnsServer(vstoreId).pipe(
            map(({ data }) => {
                const ips = this.util.deserialize(data.ADDRESS, []);
                config.configured = ips.filter(Boolean).length !== 0;
                return config;
            }),
            catchError(() => of(config)),
        );
    }

    getAdDomainConfig(vstoreId: string): Observable<GuideConfig> {
        const config: GuideConfig = {
            name: GuideItemE.AD,
            label: this.i18nService.get('settings.domainAuth_term_adDomain_tilte'),
            configured: false,
            permissionTips: this.getNopermissionTips(['domain_W']),
            action: (cb) => {
                this.lazyLoadService.appendComponent(ConfigureServiceAdComponent, {
                    vstore: { ID: vstoreId },
                    onClose: (args) => {
                        if (args && args.refresh) {
                            cb();
                        }
                    },
                });
            },
        };

        if (!this.hasDomainReadPermission) {
            return of(config);
        }

        return this.serviceAdService.get(vstoreId).pipe(
            map(({ data }) => {
                config.configured = Boolean(data.FULLDOMAINNAME);
                return config;
            }),
            catchError(() => of(config)),
        );
    }

    getNisDomainConfig(vstoreId: string): Observable<GuideConfig> {
        const config: GuideConfig = {
            name: GuideItemE.NIS,
            label: this.i18nService.get('service.share_term_addClient_configDomain_NIS'),
            configured: false,
            permissionTips: this.getNopermissionTips(['domain_W']),
            action: (cb) => {
                this.lazyLoadService.appendComponent(ConfigureServiceNisComponent, {
                    vstoreId,
                    onClose: cb,
                });
            },
        };

        if (!this.hasDomainReadPermission) {
            return of(config);
        }

        return this.serviceNisService.get(vstoreId).pipe(
            map(({ data }) => {
                config.configured = Boolean(data.primaryIp);
                return config;
            }),
            catchError(() => of(config)),
        );
    }

    getLDAPDomainConfig(vstoreId: string): Observable<GuideConfig> {
        const config: GuideConfig = {
            name: GuideItemE.LDAP,
            label: this.i18nService.get('service.share_term_addClient_configDomain_LDAP'),
            configured: false,
            permissionTips: this.getNopermissionTips(['domain_W']),
            action: (cb) => {
                this.lazyLoadService.appendComponent(ConfigureServiceLdapComponent, {
                    vstoreId,
                    onClose: cb,
                });
            },
        };

        if (!this.hasDomainReadPermission) {
            return of(config);
        }

        return this.serviceLdapService.get(vstoreId).pipe(
            map(({ data }) => {
                config.configured = Boolean(data.primaryIp);
                return config;
            }),
            catchError(() => of(config)),
        );
    }

    getNopermissionTips(permissions: string[]): string {
        const noPermissionTips = (noPermission: string[]): string =>
            this.i18nService.get('common.noPermission_err_tip', [noPermission.join(this.i18nService.get('common.comma_label'))]);
        const noPermissions = this.permissionService.getNoPermissions(permissions);
        return noPermissions.length ? noPermissionTips(noPermissions) : '';
    }
}
